/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DropdownModel } from 'src/app/core/models';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-country-dropdown',
    templateUrl: './country-dropdown.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => CountryDropdownComponent),
        }
    ]
})

export class CountryDropdownComponent extends BaseComponent implements ControlValueAccessor {
    /**
     * Form control name used to associate this control with the parent control.
     */
    @Input() public formControlName: string;

    /**
     * Our form group
     */
    @Input() set formGroup(value: FormGroup) {
        // Set our local form group variable
        this._formGroup = value;

        // Create a change subscriber for our form group
        this._formGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    if (!this._formGroup.touched) {
                        this.selectedItem = undefined;
                    }
                }
            });
    }

    /**
     * selected key used to associate this selected key with the parent selected key
     */
    @Input() public selectedKey: any = undefined;

    /**
     * Direction the dropdown should open in.
     */
    @Input() public direction: 'up' | 'down' = 'down';

    /**
     * To store height for the dropdown.
     */
    @Input() public dropdownHeight: number;

    /**
     * To store width for the dropdown.
     */
    @Input() public dropdownWidth: number;

    /**
     * Our selected key/value object
     */
    public selectedItem: DropdownModel;

    /**
     * List of countries for displaying to the user.
     */
    public countriesArray = [
        <DropdownModel>{ key: 'AX', value: 'Åland Islands' },
        <DropdownModel>{ key: 'AF', value: 'Afghanistan' },
        <DropdownModel>{ key: 'AL', value: 'Albania' },
        <DropdownModel>{ key: 'DZ', value: 'Algeria' },
        <DropdownModel>{ key: 'AS', value: 'American Samoa' },
        <DropdownModel>{ key: 'AD', value: 'Andorra' },
        <DropdownModel>{ key: 'AO', value: 'Angola' },
        <DropdownModel>{ key: 'AI', value: 'Anguilla' },
        <DropdownModel>{ key: 'AQ', value: 'Antarctica' },
        <DropdownModel>{ key: 'AG', value: 'Antigua and Barbuda' },
        <DropdownModel>{ key: 'AR', value: 'Argentina' },
        <DropdownModel>{ key: 'AM', value: 'Armenia' },
        <DropdownModel>{ key: 'AW', value: 'Aruba' },
        <DropdownModel>{ key: 'AU', value: 'Australia' },
        <DropdownModel>{ key: 'AT', value: 'Austria' },
        <DropdownModel>{ key: 'AZ', value: 'Azerbaijan' },
        <DropdownModel>{ key: 'BS', value: 'Bahamas' },
        <DropdownModel>{ key: 'BH', value: 'Bahrain' },
        <DropdownModel>{ key: 'BD', value: 'Bangladesh' },
        <DropdownModel>{ key: 'BB', value: 'Barbados' },
        <DropdownModel>{ key: 'BE', value: 'Belgium' },
        <DropdownModel>{ key: 'BZ', value: 'Belize' },
        <DropdownModel>{ key: 'BJ', value: 'Benin' },
        <DropdownModel>{ key: 'BM', value: 'Bermuda' },
        <DropdownModel>{ key: 'BT', value: 'Bhutan' },
        <DropdownModel>{ key: 'BO', value: 'Bolivia' },
        <DropdownModel>{ key: 'BA', value: 'Bosnia and Herzegovina' },
        <DropdownModel>{ key: 'BW', value: 'Botswana' },
        <DropdownModel>{ key: 'BV', value: 'Bouvet Island' },
        <DropdownModel>{ key: 'BR', value: 'Brazil' },
        <DropdownModel>{ key: 'IO', value: 'British Indian Ocean Territory' },
        <DropdownModel>{ key: 'BN', value: 'Brunei Darussalam' },
        <DropdownModel>{ key: 'BG', value: 'Bulgaria' },
        <DropdownModel>{ key: 'BF', value: 'Burkina Faso' },
        <DropdownModel>{ key: 'BI', value: 'Burundi' },
        <DropdownModel>{ key: 'KH', value: 'Cambodia' },
        <DropdownModel>{ key: 'CM', value: 'Cameroon' },
        <DropdownModel>{ key: 'CA', value: 'Canada' },
        <DropdownModel>{ key: 'CV', value: 'Cape Verde' },
        <DropdownModel>{ key: 'KY', value: 'Cayman Islands' },
        <DropdownModel>{ key: 'CF', value: 'Central African Republic' },
        <DropdownModel>{ key: 'TD', value: 'Chad' },
        <DropdownModel>{ key: 'CL', value: 'Chile' },
        <DropdownModel>{ key: 'CN', value: 'China' },
        <DropdownModel>{ key: 'CX', value: 'Christmas Island' },
        <DropdownModel>{ key: 'CC', value: 'Cocos (Keeling) Islands' },
        <DropdownModel>{ key: 'CO', value: 'Colombia' },
        <DropdownModel>{ key: 'KM', value: 'Comoros' },
        <DropdownModel>{ key: 'CK', value: 'Cook Islands' },
        <DropdownModel>{ key: 'CR', value: 'Costa Rica' },
        <DropdownModel>{ key: 'CI', value: 'Cote D\'Ivoire (Ivory Coast)' },
        <DropdownModel>{ key: 'HR', value: 'Croatia (Hrvatska)' },
        <DropdownModel>{ key: 'CY', value: 'Cyprus' },
        <DropdownModel>{ key: 'CZ', value: 'Czech Republic' },
        <DropdownModel>{ key: 'CS', value: 'Czechoslovakia (former)' },
        <DropdownModel>{ key: 'DK', value: 'Denmark' },
        <DropdownModel>{ key: 'DJ', value: 'Djibouti' },
        <DropdownModel>{ key: 'DM', value: 'Dominica' },
        <DropdownModel>{ key: 'DO', value: 'Dominican Republic' },
        <DropdownModel>{ key: 'TP', value: 'East Timor' },
        <DropdownModel>{ key: 'EC', value: 'Ecuador' },
        <DropdownModel>{ key: 'EG', value: 'Egypt' },
        <DropdownModel>{ key: 'SV', value: 'El Salvador' },
        <DropdownModel>{ key: 'GQ', value: 'Equatorial Guinea' },
        <DropdownModel>{ key: 'ER', value: 'Eritrea' },
        <DropdownModel>{ key: 'EE', value: 'Estonia' },
        <DropdownModel>{ key: 'ET', value: 'Ethiopia' },
        <DropdownModel>{ key: 'FK', value: 'Falkland Islands' },
        <DropdownModel>{ key: 'FO', value: 'Faroe Islands' },
        <DropdownModel>{ key: 'FJ', value: 'Fiji' },
        <DropdownModel>{ key: 'FI', value: 'Finland' },
        <DropdownModel>{ key: 'FR', value: 'France' },
        <DropdownModel>{ key: 'FX', value: 'France, Metropolitan' },
        <DropdownModel>{ key: 'GF', value: 'French Guiana' },
        <DropdownModel>{ key: 'PF', value: 'French Polynesia' },
        <DropdownModel>{ key: 'TF', value: 'French Southern Territories' },
        <DropdownModel>{ key: 'GA', value: 'Gabon' },
        <DropdownModel>{ key: 'GM', value: 'Gambia' },
        <DropdownModel>{ key: 'GE', value: 'Georgia' },
        <DropdownModel>{ key: 'DE', value: 'Germany' },
        <DropdownModel>{ key: 'GH', value: 'Ghana' },
        <DropdownModel>{ key: 'GI', value: 'Gibraltar' },
        <DropdownModel>{ key: 'GB', value: 'Great Britain (UK)' },
        <DropdownModel>{ key: 'GR', value: 'Greece' },
        <DropdownModel>{ key: 'GL', value: 'Greenland' },
        <DropdownModel>{ key: 'GD', value: 'Grenada' },
        <DropdownModel>{ key: 'GP', value: 'Guadeloupe' },
        <DropdownModel>{ key: 'GU', value: 'Guam' },
        <DropdownModel>{ key: 'GT', value: 'Guatemala' },
        <DropdownModel>{ key: 'GG', value: 'Guernsey' },
        <DropdownModel>{ key: 'GN', value: 'Guinea' },
        <DropdownModel>{ key: 'GW', value: 'Guinea-Bissau' },
        <DropdownModel>{ key: 'GY', value: 'Guyana' },
        <DropdownModel>{ key: 'HT', value: 'Haiti' },
        <DropdownModel>{ key: 'HM', value: 'Heard and McDonald Islands' },
        <DropdownModel>{ key: 'HN', value: 'Honduras' },
        <DropdownModel>{ key: 'HK', value: 'Hong Kong' },
        <DropdownModel>{ key: 'HU', value: 'Hungary' },
        <DropdownModel>{ key: 'IS', value: 'Iceland' },
        <DropdownModel>{ key: 'IN', value: 'India' },
        <DropdownModel>{ key: 'ID', value: 'Indonesia' },
        <DropdownModel>{ key: 'IE', value: 'Ireland' },
        <DropdownModel>{ key: 'IM', value: 'Isle of Man' },
        <DropdownModel>{ key: 'IL', value: 'Israel' },
        <DropdownModel>{ key: 'IT', value: 'Italy' },
        <DropdownModel>{ key: 'JM', value: 'Jamaica' },
        <DropdownModel>{ key: 'JP', value: 'Japan' },
        <DropdownModel>{ key: 'JE', value: 'Jersey' },
        <DropdownModel>{ key: 'JO', value: 'Jordan' },
        <DropdownModel>{ key: 'KZ', value: 'Kazakhstan' },
        <DropdownModel>{ key: 'KE', value: 'Kenya' },
        <DropdownModel>{ key: 'KI', value: 'Kiribati' },
        <DropdownModel>{ key: 'KR', value: 'Korea (South)' },
        <DropdownModel>{ key: 'KW', value: 'Kuwait' },
        <DropdownModel>{ key: 'KG', value: 'Kyrgyzstan' },
        <DropdownModel>{ key: 'LA', value: 'Laos' },
        <DropdownModel>{ key: 'LV', value: 'Latvia' },
        <DropdownModel>{ key: 'LS', value: 'Lesotho' },
        <DropdownModel>{ key: 'LY', value: 'Libya' },
        <DropdownModel>{ key: 'LI', value: 'Liechtenstein' },
        <DropdownModel>{ key: 'LT', value: 'Lithuania' },
        <DropdownModel>{ key: 'LU', value: 'Luxembourg' },
        <DropdownModel>{ key: 'MO', value: 'Macau' },
        <DropdownModel>{ key: 'MK', value: 'Macedonia' },
        <DropdownModel>{ key: 'MG', value: 'Madagascar' },
        <DropdownModel>{ key: 'MW', value: 'Malawi' },
        <DropdownModel>{ key: 'MY', value: 'Malaysia' },
        <DropdownModel>{ key: 'MV', value: 'Maldives' },
        <DropdownModel>{ key: 'ML', value: 'Mali' },
        <DropdownModel>{ key: 'MT', value: 'Malta' },
        <DropdownModel>{ key: 'MH', value: 'Marshall Islands' },
        <DropdownModel>{ key: 'MQ', value: 'Martinique' },
        <DropdownModel>{ key: 'MR', value: 'Mauritania' },
        <DropdownModel>{ key: 'MU', value: 'Mauritius' },
        <DropdownModel>{ key: 'YT', value: 'Mayotte' },
        <DropdownModel>{ key: 'MX', value: 'Mexico' },
        <DropdownModel>{ key: 'FM', value: 'Micronesia' },
        <DropdownModel>{ key: 'MD', value: 'Moldova' },
        <DropdownModel>{ key: 'MC', value: 'Monaco' },
        <DropdownModel>{ key: 'MN', value: 'Mongolia' },
        <DropdownModel>{ key: 'ME', value: 'Montenegro' },
        <DropdownModel>{ key: 'MS', value: 'Montserrat' },
        <DropdownModel>{ key: 'MA', value: 'Morocco' },
        <DropdownModel>{ key: 'MZ', value: 'Mozambique' },
        <DropdownModel>{ key: 'MM', value: 'Myanmar' },
        <DropdownModel>{ key: 'NA', value: 'Namibia' },
        <DropdownModel>{ key: 'NR', value: 'Nauru' },
        <DropdownModel>{ key: 'NP', value: 'Nepal' },
        <DropdownModel>{ key: 'NL', value: 'Netherlands' },
        <DropdownModel>{ key: 'AN', value: 'Netherlands Antilles' },
        <DropdownModel>{ key: 'NT', value: 'Neutral Zone' },
        <DropdownModel>{ key: 'NC', value: 'New Caledonia' },
        <DropdownModel>{ key: 'NZ', value: 'New Zealand (Aotearoa)' },
        <DropdownModel>{ key: 'NI', value: 'Nicaragua' },
        <DropdownModel>{ key: 'NE', value: 'Niger' },
        <DropdownModel>{ key: 'NG', value: 'Nigeria' },
        <DropdownModel>{ key: 'NU', value: 'Niue' },
        <DropdownModel>{ key: 'NF', value: 'Norfolk Island' },
        <DropdownModel>{ key: 'MP', value: 'Northern Mariana Islands' },
        <DropdownModel>{ key: 'NO', value: 'Norway' },
        <DropdownModel>{ key: 'OM', value: 'Oman' },
        <DropdownModel>{ key: 'PK', value: 'Pakistan' },
        <DropdownModel>{ key: 'PW', value: 'Palau' },
        <DropdownModel>{ key: 'PS', value: 'Palestinian Territory' },
        <DropdownModel>{ key: 'PA', value: 'Panama' },
        <DropdownModel>{ key: 'PG', value: 'Papua New Guinea' },
        <DropdownModel>{ key: 'PY', value: 'Paraguay' },
        <DropdownModel>{ key: 'PE', value: 'Peru' },
        <DropdownModel>{ key: 'PH', value: 'Philippines' },
        <DropdownModel>{ key: 'PN', value: 'Pitcairn' },
        <DropdownModel>{ key: 'PL', value: 'Poland' },
        <DropdownModel>{ key: 'PT', value: 'Portugal' },
        <DropdownModel>{ key: 'PR', value: 'Puerto Rico' },
        <DropdownModel>{ key: 'QA', value: 'Qatar' },
        <DropdownModel>{ key: 'RE', value: 'Reunion' },
        <DropdownModel>{ key: 'RO', value: 'Romania' },
        <DropdownModel>{ key: 'RU', value: 'Russian Federation' },
        <DropdownModel>{ key: 'RW', value: 'Rwanda' },
        <DropdownModel>{ key: 'GS', value: 'S. Georgia and S. Sandwich Isls.' },
        <DropdownModel>{ key: 'KN', value: 'Saint Kitts and Nevis' },
        <DropdownModel>{ key: 'LC', value: 'Saint Lucia' },
        <DropdownModel>{ key: 'VC', value: 'Saint Vincent and the Grenadines' },
        <DropdownModel>{ key: 'WS', value: 'Samoa' },
        <DropdownModel>{ key: 'SM', value: 'San Marino' },
        <DropdownModel>{ key: 'ST', value: 'Sao Tome and Principe' },
        <DropdownModel>{ key: 'SA', value: 'Saudi Arabia' },
        <DropdownModel>{ key: 'SN', value: 'Senegal' },
        <DropdownModel>{ key: 'RS', value: 'Serbia' },
        <DropdownModel>{ key: 'SC', value: 'Seychelles' },
        <DropdownModel>{ key: 'SL', value: 'Sierra Leone' },
        <DropdownModel>{ key: 'SG', value: 'Singapore' },
        <DropdownModel>{ key: 'SK', value: 'Slovak Republic' },
        <DropdownModel>{ key: 'SI', value: 'Slovenia' },
        <DropdownModel>{ key: 'SB', value: 'Solomon Islands' },
        <DropdownModel>{ key: 'ZA', value: 'South Africa' },
        <DropdownModel>{ key: 'ES', value: 'Spain' },
        <DropdownModel>{ key: 'LK', value: 'Sri Lanka' },
        <DropdownModel>{ key: 'SH', value: 'St. Helena' },
        <DropdownModel>{ key: 'PM', value: 'St. Pierre and Miquelon' },
        <DropdownModel>{ key: 'SR', value: 'Suriname' },
        <DropdownModel>{ key: 'SJ', value: 'Svalbard and Jan Mayen Islands' },
        <DropdownModel>{ key: 'SZ', value: 'Swaziland' },
        <DropdownModel>{ key: 'SE', value: 'Sweden' },
        <DropdownModel>{ key: 'CH', value: 'Switzerland' },
        <DropdownModel>{ key: 'TW', value: 'Taiwan' },
        <DropdownModel>{ key: 'TJ', value: 'Tajikistan' },
        <DropdownModel>{ key: 'TZ', value: 'Tanzania' },
        <DropdownModel>{ key: 'TH', value: 'Thailand' },
        <DropdownModel>{ key: 'TG', value: 'Togo' },
        <DropdownModel>{ key: 'TK', value: 'Tokelau' },
        <DropdownModel>{ key: 'TO', value: 'Tonga' },
        <DropdownModel>{ key: 'TT', value: 'Trinidad and Tobago' },
        <DropdownModel>{ key: 'TN', value: 'Tunisia' },
        <DropdownModel>{ key: 'TR', value: 'Turkey' },
        <DropdownModel>{ key: 'TM', value: 'Turkmenistan' },
        <DropdownModel>{ key: 'TC', value: 'Turks and Caicos Islands' },
        <DropdownModel>{ key: 'TV', value: 'Tuvalu' },
        <DropdownModel>{ key: 'UM', value: 'US Minor Outlying Islands' },
        <DropdownModel>{ key: 'SU', value: 'USSR (former)' },
        <DropdownModel>{ key: 'UG', value: 'Uganda' },
        <DropdownModel>{ key: 'UA', value: 'Ukraine' },
        <DropdownModel>{ key: 'AE', value: 'United Arab Emirates' },
        <DropdownModel>{ key: 'US', value: 'United States' },
        <DropdownModel>{ key: 'UY', value: 'Uruguay' },
        <DropdownModel>{ key: 'UZ', value: 'Uzbekistan' },
        <DropdownModel>{ key: 'VU', value: 'Vanuatu' },
        <DropdownModel>{ key: 'VA', value: 'Vatican City State' },
        <DropdownModel>{ key: 'VE', value: 'Venezuela' },
        <DropdownModel>{ key: 'VN', value: 'Vietnam' },
        <DropdownModel>{ key: 'VG', value: 'Virgin Islands (British)' },
        <DropdownModel>{ key: 'VI', value: 'Virgin Islands (U.S.)' },
        <DropdownModel>{ key: 'WF', value: 'Wallis and Futuna Islands' },
        <DropdownModel>{ key: 'EH', value: 'Western Sahara' },
        <DropdownModel>{ key: 'YE', value: 'Yemen' },
        <DropdownModel>{ key: 'ZM', value: 'Zambia' }
    ];

    constructor() {
        super();
    }

    /**
     * Our from group literal
     * @private
     */
    private _formGroup: FormGroup;

    public setCountryFormValue($event) {
        this._formGroup.get(this.formControlName).setValue($event);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public registerOnChange(fn: any): void {
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public registerOnTouched(fn: any): void {
    }

    public writeValue(key: any): void {
        this.selectedKey = key;
    }
}

